import { Box, Heading, Text } from '@chakra-ui/layout';
import { Button, Input, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { StyledDropzone } from '../components/StyledDropzone';
import SelectedGameContext from '../context/SelectedGameContext';
import * as yup from 'yup';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import { uploadToS3 } from '../util/depot';
import { AssetFileType } from '../util/depot';

const AddCSharpActorDepotSchema = yup.object().shape({
    description: yup.string().required('Description is required'),
    ms: yup.mixed().required('Mothership file is required'),
    sat: yup.mixed().required('Satellite file is required'),
});

export const AddCSharpActorDepot: React.FC<{}> = () => {
    const selectedGame = useContext(SelectedGameContext);
    const [description, setDescription] = useState('');
    const [isMsSet, setMsReady] = useState(false);
    const [isSatSet, setSatReady] = useState(false);
    const [msCaption, setMsCaption] = useState(
        "Drag 'n' drop mothership actor executable (x64 Linux binary) here, or click to select a file",
    );
    const [satCaption, setSatCaption] = useState(
        "Drag 'n' drop satellite actor executable (x64 Linux binary) here, or click to select a file",
    );
    const { keycloak } = useKeycloak();
    const toast = useToast();
    const navigate = useNavigate();

    const _uploadToS3 = async (desc: string, msFile: any, satFile: any) => {
        const uploadingToastId = toast({
            title: 'Uploading...',
            status: 'info',
            duration: null,
            isClosable: true,
        });

        try {
            const fileTypes = [AssetFileType.mothership, AssetFileType.satellite];
            const files = [msFile, satFile];
            const buildData = await uploadToS3(
                selectedGame.currLicense.key,
                desc,
                fileTypes,
                files,
                keycloak.token,
            );
            toast({
                title: 'Success!',
                description: `Build ${buildData?.buildId} uploaded successfully`,
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
            toast.close(uploadingToastId || '');
            navigate(`/game/${selectedGame.currLicense.key}/csharp-actor`, {
                replace: true,
            });
        } catch (err) {
            return toast({
                title: 'Error',
                description: err instanceof Error ? err.message : `Unexpected error ${err}`,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            toast.close(uploadingToastId || '');
        }
    };

    return (
        <Box>
            <Heading>Upload C# Actor</Heading>
            <Formik
                initialValues={{
                    description: '',
                    ms: null,
                    sat: null,
                }}
                validationSchema={AddCSharpActorDepotSchema}
                onSubmit={async (values) => {
                    await _uploadToS3(values.description, values.ms, values.sat);
                }}
            >
                {({ errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => {
                    const onMothershipSelected = (acceptedFiles: any) => {
                        setFieldValue('ms', acceptedFiles[0]);
                        setMsCaption(acceptedFiles[0].name);
                        setMsReady(true);
                    };

                    const onSatelliteSelected = (acceptedFiles: any) => {
                        setFieldValue('sat', acceptedFiles[0]);
                        setSatCaption(acceptedFiles[0].name);
                        setSatReady(true);
                    };

                    return (
                        <Form
                            action=""
                            style={{
                                padding: '1em',
                            }}
                        >
                            <Heading as="h2" size="md" paddingBottom="0.5em">
                                Mothership Executable
                            </Heading>
                            <Text color="red">{errors.ms && touched.ms && errors.ms}</Text>
                            <StyledDropzone
                                name="ms"
                                caption={msCaption}
                                onDrop={onMothershipSelected}
                            />

                            <Heading as="h2" size="md" paddingBottom="0.5em">
                                Satellite Executable
                            </Heading>
                            <Text color="red">{errors.sat && touched.sat && errors.sat}</Text>
                            <StyledDropzone
                                name="sat"
                                caption={satCaption}
                                onDrop={onSatelliteSelected}
                            />

                            <Heading as="h2" size="md" paddingBottom="0.5em">
                                Description
                            </Heading>
                            <Text color="red">
                                {errors.description && touched.description && errors.description}
                            </Text>
                            <Input
                                name="description"
                                placeholder="Short description of this build, such as version number"
                                marginBottom="0.5em"
                                onChange={(e) => {
                                    setDescription(e.target.value);
                                    handleChange(e);
                                }}
                                value={description}
                                onBlur={(e) => {
                                    handleBlur(e);
                                }}
                            />

                            <Button
                                type="submit"
                                marginTop="0.5em"
                                disabled={isSubmitting || !isMsSet || !isSatSet || !description}
                            >
                                Upload
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
        </Box>
    );
};
